import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, AlertCircle, UserCheck, Check, X, RotateCcw, Pencil, ListTree, ChevronLeft, ChevronRight } from 'lucide-react';
import { laborScheduleService } from '../../calendario/services/laborScheduleService';
import { LaborAttendanceValidationModal } from './LaborAttendanceValidationModal';
import { LaborAttendanceSegmentsModal } from './LaborAttendanceSegmentsModal';
import { flattenAttendanceCollection } from '../utils/attendanceDateUtils';

const STATUS_CONFIG = {
  present: {
    label: 'Presente',
    className: 'bg-emerald-900/50 text-emerald-300 border border-emerald-700',
  },
  late: {
    label: 'Tarde',
    className: 'bg-yellow-900/50 text-yellow-300 border border-yellow-700',
  },
  absent: {
    label: 'Ausente',
    className: 'bg-red-900/50 text-red-300 border border-red-700',
  },
  incomplete: {
    label: 'Incompleto',
    className: 'bg-amber-900/50 text-amber-300 border border-amber-700',
  },
  justified: {
    label: 'Justificado',
    className: 'bg-blue-900/50 text-blue-300 border border-blue-700',
  },
  rejected: {
    label: 'Rechazado',
    className: 'bg-red-900/50 text-red-300 border border-red-700',
  },
};

const VALIDATION_CONFIG = {
  pending: {
    label: 'Pendiente',
    className: 'bg-gray-700 text-gray-300 border border-gray-600',
  },
  approved: {
    label: 'Aprobado',
    className: 'bg-emerald-900/50 text-emerald-300 border border-emerald-700',
  },
  rejected: {
    label: 'Rechazado',
    className: 'bg-red-900/50 text-red-300 border border-red-700',
  },
};

function formatTime(isoString) {
  if (!isoString) return '—';
  try {
    return new Date(isoString).toLocaleTimeString('es-CL', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'America/Santiago',
    });
  } catch {
    return '—';
  }
}

function formatOrigin(origin) {
  if (!origin) return '—';
  if (origin === 'raw_system') return 'Sistema';
  return origin;
}

function formatOriginCombined(originIn, originOut) {
  const inLabel = formatOrigin(originIn);
  const outLabel = formatOrigin(originOut);
  if (inLabel === outLabel) return inLabel;
  if (inLabel === '—') return outLabel;
  if (outLabel === '—') return inLabel;
  return `${inLabel} / ${outLabel}`;
}

const ACTION_BTN_BASE =
  'inline-flex items-center justify-center p-2 rounded-lg border cursor-pointer transition-all duration-150 active:scale-95 focus:outline-none focus-visible:ring-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100';

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50];
const DEFAULT_PAGE_SIZE = 10;

function StatusBadge({ status, isAbsent }) {
  if (isAbsent && !status) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-900/50 text-orange-300 border border-orange-700">
        Ausencia prog.
      </span>
    );
  }
  const config = STATUS_CONFIG[status];
  if (!config) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-700 text-gray-400 border border-gray-600">
        Sin registro
      </span>
    );
  }
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.className}`}>
      {config.label}
    </span>
  );
}

function ValidationBadge({ status }) {
  if (!status) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-800 text-gray-500 border border-gray-700">
        Sin registro
      </span>
    );
  }
  const cfg = VALIDATION_CONFIG[status] ?? VALIDATION_CONFIG.pending;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cfg.className}`}>
      {cfg.label}
    </span>
  );
}

function canApproveOrReject(emp) {
  if (emp.can_validate_from_raw) return true;
  return Boolean(
    emp.attendance_id &&
      (emp.validation_status === 'pending' || !emp.validation_status)
  );
}

function canRevert(emp) {
  return (
    Boolean(emp.attendance_id) &&
    (emp.validation_status === 'approved' || emp.validation_status === 'rejected')
  );
}

function clockOverrideIso(iso, rawIso) {
  if (!iso) return null;
  if (!rawIso) return iso;
  const a = new Date(iso);
  const b = new Date(rawIso);
  if (
    a.getHours() === b.getHours() &&
    a.getMinutes() === b.getMinutes() &&
    a.getDate() === b.getDate()
  ) {
    return null;
  }
  return iso;
}

function rowKey(emp) {
  return `${emp.employee_id}-${emp.row_date || ''}`;
}

/**
 * Tabla de asistencia planificada vs real para un patrón y rango de fechas.
 */
export function LaborAttendanceTable({
  rosterEmployeeIds = [],
  patternFilterId = '',
  dateFrom,
  dateTo,
  refetchRef,
  teamFilter = null,
  fetchEnabled = false,
  pendingMessage = '',
  rosterEmpty = false,
  rosterEmptyMessage = 'No hay empleados en el alcance seleccionado.',
  validatorEmployeeId = null,
  readOnly = false,
  onFetchingChange,
}) {
  const queryClient = useQueryClient();
  const isRange = Boolean(dateFrom && dateTo && dateFrom !== dateTo);
  const rosterKey = useMemo(
    () => [...new Set(rosterEmployeeIds.map((id) => Number(id)).filter((id) => id > 0))].sort((a, b) => a - b).join(','),
    [rosterEmployeeIds]
  );
  const queryKey = ['labor-attendance-roster', rosterKey, dateFrom, dateTo];

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey,
    queryFn: () => laborScheduleService.getAttendanceRoster(
      rosterKey.split(',').map(Number),
      dateFrom,
      dateTo,
    ),
    enabled: fetchEnabled && rosterKey.length > 0 && Boolean(dateFrom) && Boolean(dateTo),
    staleTime: 15_000,
  });

  const { employees: loadedEmployees } = useMemo(
    () => flattenAttendanceCollection(data ? [data] : [], true),
    [data]
  );

  const patternScopedEmployees = useMemo(() => {
    if (!patternFilterId) return loadedEmployees;
    if (patternFilterId === 'none') {
      return loadedEmployees.filter((emp) => !emp.pattern_id);
    }
    return loadedEmployees.filter((emp) => String(emp.pattern_id) === String(patternFilterId));
  }, [loadedEmployees, patternFilterId]);

  const employees = useMemo(() => {
    if (!teamFilter) return patternScopedEmployees;
    if (!teamFilter.employeeIds?.size) return [];
    return patternScopedEmployees.filter((emp) =>
      teamFilter.employeeIds.has(Number(emp.employee_id))
    );
  }, [patternScopedEmployees, teamFilter]);

  const visiblePatternName = !patternFilterId
    ? 'Todos los empleados'
    : patternFilterId === 'none'
      ? 'Sin patrón'
      : (patternScopedEmployees.find((emp) => String(emp.pattern_id) === String(patternFilterId))?.pattern_name || 'Patrón');

  // Cuando el filtro deja la tabla vacía y hay exactamente un empleado seleccionado,
  // se consulta su patrón activo y su horario efectivo para ese día para poder explicar
  // la causa exacta: sin patrón/turno asignado, o con patrón pero día de descanso.
  const singleEmployeeId = useMemo(() => {
    const ids = teamFilter?.employeeIds;
    if (!ids || ids.size !== 1) return null;
    return [...ids][0];
  }, [teamFilter]);

  const diagnosisEnabled = employees.length === 0 && Boolean(singleEmployeeId) && Boolean(dateFrom);

  const { data: emptyStateDiagnosis, isLoading: loadingDiagnosis } = useQuery({
    queryKey: ['labor-attendance-empty-diagnosis', singleEmployeeId, dateFrom],
    queryFn: async () => {
      const [activePattern, daySchedule] = await Promise.all([
        laborScheduleService.getEmployeeActivePattern(singleEmployeeId, dateFrom),
        laborScheduleService.getEmployeeDaySchedule(singleEmployeeId, dateFrom),
      ]);
      return {
        hasPattern: Boolean(activePattern?.pattern_id),
        patternName: activePattern?.pattern_name || '',
        isWorkingDay: Boolean(daySchedule?.IsWorkingDay),
        shiftCode: daySchedule?.ShiftCode || '',
      };
    },
    enabled: diagnosisEnabled,
  });

  useEffect(() => {
    if (refetchRef) refetchRef.current = refetch;
  }, [refetch, refetchRef]);

  useEffect(() => {
    onFetchingChange?.(isFetching);
  }, [isFetching, onFetchingChange]);

  const [validationTarget, setValidationTarget] = useState(null);
  const [mutatingEmployeeId, setMutatingEmployeeId] = useState(null);
  const [segmentsTarget, setSegmentsTarget] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [rosterKey, dateFrom, dateTo, patternFilterId, teamFilter?.teamId]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey });

  const validateFromRawMutation = useMutation({
    mutationFn: ({ employeeId, rowDate, patternId: rowPatternId, action, notes, observations, clockIn, clockOut }) =>
      laborScheduleService.validateAttendanceFromRaw({
        employeeId,
        date: rowDate,
        patternId: rowPatternId,
        action,
        notes,
        observations,
        validatorEmployeeId,
        clockIn,
        clockOut,
      }),
    onSuccess: invalidate,
  });

  const validateMutation = useMutation({
    mutationFn: ({ attendanceId, action, notes }) =>
      laborScheduleService.validateAttendance(attendanceId, {
        action,
        notes,
        validatorEmployeeId,
      }),
    onSuccess: invalidate,
  });

  const revertMutation = useMutation({
    mutationFn: ({ attendanceId }) =>
      laborScheduleService.revertAttendanceValidation(attendanceId, { validatorEmployeeId }),
    onSuccess: invalidate,
  });

  const bulkApproveMutation = useMutation({
    mutationFn: async (groups) => {
      for (const { rowDate, patternId: rowPatternId, employeeIds } of groups) {
        if (employeeIds.length > 0) {
          await laborScheduleService.bulkApproveAttendanceFromRaw({
            employeeIds,
            date: rowDate,
            patternId: rowPatternId,
            validatorEmployeeId,
          });
        }
      }
    },
    onSuccess: invalidate,
  });

  const bulkEligibleByDate = useMemo(() => {
    const map = new Map();
    for (const emp of employees) {
      if (!emp.can_validate_from_raw || !emp.clock_in_raw || !emp.clock_out_raw) continue;
      const key = `${emp.row_date}|${emp.pattern_id || ''}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(emp.employee_id);
    }
    return [...map.entries()].map(([key, employeeIds]) => {
      const [rowDate, rowPatternId] = key.split('|');
      return { rowDate, patternId: rowPatternId, employeeIds };
    });
  }, [employees]);

  const bulkEligibleCount = useMemo(
    () => bulkEligibleByDate.reduce((sum, g) => sum + g.employeeIds.length, 0),
    [bulkEligibleByDate]
  );

  const canValidate = !readOnly && Boolean(validatorEmployeeId);

  const handleApproveOpen = (emp) => {
    setValidationTarget({
      mode: 'approve',
      employeeId: emp.employee_id,
      rowDate: emp.row_date,
      attendanceId: emp.attendance_id,
      employeeName: emp.employee_name,
      plannedStart: emp.planned_start,
      plannedEnd: emp.planned_end,
      clockInRaw: emp.clock_in_raw,
      clockOutRaw: emp.clock_out_raw,
      patternId: emp.pattern_id,
      fromRaw: Boolean(emp.can_validate_from_raw),
    });
  };

  const handleCorrectOpen = (emp) => {
    setValidationTarget({
      mode: 'correct',
      employeeId: emp.employee_id,
      rowDate: emp.row_date,
      patternId: emp.pattern_id,
      employeeName: emp.employee_name,
      plannedStart: emp.planned_start,
      plannedEnd: emp.planned_end,
      clockInRaw: emp.clock_in_raw,
      clockOutRaw: emp.clock_out_raw,
      fromRaw: false,
    });
  };

  const handleRejectOpen = (emp) => {
    setValidationTarget({
      mode: 'reject',
      employeeId: emp.employee_id,
      rowDate: emp.row_date,
      attendanceId: emp.attendance_id,
      employeeName: emp.employee_name,
      plannedStart: emp.planned_start,
      plannedEnd: emp.planned_end,
      clockInRaw: emp.clock_in_raw,
      clockOutRaw: emp.clock_out_raw,
      patternId: emp.pattern_id,
      fromRaw: Boolean(emp.can_validate_from_raw),
    });
  };

  const handleValidationConfirm = async ({ observations, clockIn, clockOut }) => {
    if (!validationTarget) return;
    setMutatingEmployeeId(`${validationTarget.employeeId}-${validationTarget.rowDate || ''}`);
    try {
      if (validationTarget.mode === 'approve' || validationTarget.mode === 'correct') {
        let sendClockIn = null;
        let sendClockOut = null;
        if (validationTarget.mode === 'correct') {
          sendClockIn = clockIn;
          sendClockOut = clockOut;
        } else {
          sendClockIn = validationTarget.clockInRaw
            ? clockOverrideIso(clockIn, validationTarget.clockInRaw)
            : clockIn;
          sendClockOut = validationTarget.clockOutRaw
            ? clockOverrideIso(clockOut, validationTarget.clockOutRaw)
            : clockOut;
        }

        if (validationTarget.fromRaw || validationTarget.mode === 'correct') {
          await validateFromRawMutation.mutateAsync({
            employeeId: validationTarget.employeeId,
            rowDate: validationTarget.rowDate,
            patternId: validationTarget.patternId,
            action: 'approve',
            observations,
            clockIn: sendClockIn,
            clockOut: sendClockOut,
          });
        } else {
          await validateMutation.mutateAsync({
            attendanceId: validationTarget.attendanceId,
            action: 'approve',
            notes: observations,
          });
        }
      } else if (validationTarget.fromRaw) {
        await validateFromRawMutation.mutateAsync({
          employeeId: validationTarget.employeeId,
          rowDate: validationTarget.rowDate,
          patternId: validationTarget.patternId,
          action: 'reject',
          notes: observations,
        });
      } else {
        await validateMutation.mutateAsync({
          attendanceId: validationTarget.attendanceId,
          action: 'reject',
          notes: observations,
        });
      }
    } finally {
      setMutatingEmployeeId(null);
      setValidationTarget(null);
    }
  };

  const handleRevert = (emp) => {
    revertMutation.mutate({ attendanceId: emp.attendance_id });
  };

  const handleSegmentsOpen = (emp) => {
    setSegmentsTarget({
      employeeId: emp.employee_id,
      employeeName: emp.employee_name,
      date: emp.row_date,
    });
  };

  const handleBulkApprove = () => {
    if (bulkEligibleByDate.length) bulkApproveMutation.mutate(bulkEligibleByDate);
  };

  if (!dateFrom || !dateTo) {
    return (
      <div className="text-center text-gray-500 py-12 border border-dashed border-gray-600 rounded-lg">
        Indica un rango de fechas para ver la asistencia.
      </div>
    );
  }

  if (!fetchEnabled) {
    return (
      <div className="text-center text-gray-500 py-12 border border-dashed border-gray-600 rounded-lg">
        {pendingMessage ? (
          <span className="inline-flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            {pendingMessage}
          </span>
        ) : rosterEmpty ? (
          rosterEmptyMessage
        ) : (
          'Selecciona los filtros para ver la asistencia.'
        )}
      </div>
    );
  }

  if (isLoading || teamFilter?.loading) {
    return (
      <div className="flex justify-center py-8 text-gray-400">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        {teamFilter?.loading ? 'Cargando miembros del equipo…' : 'Cargando asistencia…'}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 text-red-400 bg-red-900/20 border border-red-800 rounded-lg p-4">
        <AlertCircle className="w-5 h-5 shrink-0" />
        <span>{error.message || 'Error al cargar la asistencia.'}</span>
      </div>
    );
  }

  if (employees.length === 0) {
    const teamFiltered = Boolean(teamFilter);
    return (
      <div className="text-center text-gray-500 py-12 border border-dashed border-gray-600 rounded-lg">
        <UserCheck className="w-8 h-8 mx-auto mb-2 opacity-40" />
        {singleEmployeeId && loadingDiagnosis ? (
          <p className="inline-flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            Comprobando el turno del empleado…
          </p>
        ) : singleEmployeeId && emptyStateDiagnosis && !emptyStateDiagnosis.hasPattern ? (
          <>
            <p>Este empleado no aparece en el rango seleccionado.</p>
            <p className="text-xs mt-1">Comprueba la fábrica, el equipo y las fechas.</p>
          </>
        ) : singleEmployeeId && emptyStateDiagnosis && emptyStateDiagnosis.hasPattern && !emptyStateDiagnosis.isWorkingDay ? (
          <>
            <p>
              El empleado tiene el patrón «{emptyStateDiagnosis.patternName}» asignado, pero el{' '}
              {dateFrom} es día de descanso para él/ella (no le corresponde turno).
            </p>
            {isRange && (
              <p className="text-xs mt-1">
                Comprueba también el resto de días del rango: puede trabajar otros días dentro
                del periodo seleccionado.
              </p>
            )}
          </>
        ) : singleEmployeeId && emptyStateDiagnosis ? (
          <>
            <p>
              El empleado sí trabaja el {dateFrom} con el patrón «{emptyStateDiagnosis.patternName}»,
              pero no aparece con el filtro «Patrón» actual.
            </p>
            <p className="text-xs mt-1">Elige «Todos», «Sin patrón» o el turno correcto en el filtro.</p>
          </>
        ) : teamFiltered && loadedEmployees.length > 0 ? (
          <>
            <p>Ningún miembro de «{teamFilter.name}» tiene jornada en el rango seleccionado.</p>
            <p className="text-xs mt-1">Quien tiene patrón y está de descanso no se muestra.</p>
          </>
        ) : teamFiltered && teamFilter.employeeIds?.size === 0 ? (
          <>
            <p>El equipo «{teamFilter.name}» no tiene miembros asignados.</p>
            <p className="text-xs mt-1">Añade operarios al equipo en /empleados.</p>
          </>
        ) : (
          <>
            <p>No hay empleados con jornada en el rango seleccionado.</p>
            <p className="text-xs mt-1">
              Quien tiene patrón y está de descanso no se muestra. Quien no tiene patrón sí aparece.
            </p>
          </>
        )}
      </div>
    );
  }

  const totalItems = employees.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const currentPage = Math.min(page, totalPages);
  const rangeStart = (currentPage - 1) * pageSize + 1;
  const rangeEnd = Math.min(currentPage * pageSize, totalItems);
  const pageRows = employees.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <>
      <LaborAttendanceValidationModal
        open={Boolean(validationTarget)}
        mode={validationTarget?.mode ?? 'reject'}
        employeeName={validationTarget?.employeeName}
        date={validationTarget?.rowDate}
        plannedStart={validationTarget?.plannedStart}
        plannedEnd={validationTarget?.plannedEnd}
        clockInRaw={validationTarget?.clockInRaw}
        clockOutRaw={validationTarget?.clockOutRaw}
        onConfirm={handleValidationConfirm}
        onClose={() => setValidationTarget(null)}
      />

      <LaborAttendanceSegmentsModal
        open={Boolean(segmentsTarget)}
        employeeId={segmentsTarget?.employeeId}
        employeeName={segmentsTarget?.employeeName}
        date={segmentsTarget?.date}
        onClose={() => setSegmentsTarget(null)}
      />

      <div className="rounded-lg border border-gray-700 overflow-hidden">
        <div className="px-4 py-3 bg-gray-800 border-b border-gray-700 flex flex-wrap items-center gap-3">
          <span className="text-sm font-medium text-white">{visiblePatternName}</span>
          {teamFilter?.name && (
            <>
              <span className="text-xs text-gray-500">·</span>
              <span className="text-xs text-teal-300">Equipo: {teamFilter.name}</span>
            </>
          )}
          <span className="text-xs text-gray-500">·</span>
          <span className="text-xs text-gray-400">
            {employees.length} registro{employees.length !== 1 ? 's' : ''}
            {employees.length !== patternScopedEmployees.length
              ? ` (de ${patternScopedEmployees.length})`
              : ''}
          </span>
          <span className="text-xs text-gray-500">·</span>
          <span className="text-xs text-gray-400">
            {isRange ? `${dateFrom} → ${dateTo}` : dateFrom}
          </span>

          {canValidate && bulkEligibleCount > 0 && (
            <button
              type="button"
              onClick={handleBulkApprove}
              disabled={bulkApproveMutation.isPending}
              className="ml-auto inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-emerald-500/50 bg-emerald-700 hover:bg-emerald-600 hover:border-emerald-400 hover:shadow-md hover:shadow-emerald-900/40 active:scale-[0.98] text-white text-xs font-medium cursor-pointer transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/50 disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100"
            >
              <Check className="w-3.5 h-3.5" />
              {bulkApproveMutation.isPending
                ? 'Aprobando…'
                : `Aprobar con fichaje completo (${bulkEligibleCount})`}
            </button>
          )}

          {!canValidate && (
            <span className="ml-auto text-xs text-amber-400/80 italic">
              Tu usuario no está vinculado a un empleado; no puedes validar fichajes
            </span>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-800/60 text-gray-400 text-xs uppercase tracking-wider">
                {isRange && <th className="px-4 py-3 text-left">Fecha</th>}
                <th className="px-4 py-3 text-left">Empleado</th>
                <th className="px-4 py-3 text-left">Turno</th>
                <th className="px-4 py-3 text-left">Planificado</th>
                <th className="px-4 py-3 text-center">
                  <span className="text-emerald-400">Entrada</span>
                  <span className="text-gray-500 mx-1">/</span>
                  <span className="text-rose-400">Salida</span>
                  <div className="text-gray-500 font-normal normal-case tracking-normal">Raw</div>
                </th>
                <th className="px-4 py-3 text-center">
                  <span className="text-emerald-400">Entrada</span>
                  <span className="text-gray-500 mx-1">/</span>
                  <span className="text-rose-400">Salida</span>
                  <div className="text-gray-500 font-normal normal-case tracking-normal">Validado</div>
                </th>
                <th className="px-4 py-3 text-center">Origen</th>
                <th className="px-4 py-3 text-center">Estado</th>
                <th className="px-4 py-3 text-center">Validación</th>
                <th className="px-4 py-3 text-left">Observaciones</th>
                {canValidate && <th className="px-4 py-3 text-center">Acciones</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/50">
              {pageRows.map((emp) => {
                const isRejected = emp.validation_status === 'rejected';
                const showActions = canApproveOrReject(emp);
                const showCorrect = emp.can_correct_and_validate;
                const showRevert = canRevert(emp);
                const mutating = mutatingEmployeeId === rowKey(emp) ||
                  (revertMutation.isPending && revertMutation.variables?.attendanceId === emp.attendance_id);

                const hasSegmentsData = Boolean(emp.clock_in_raw || emp.clock_in_actual);

                return (
                  <tr
                    key={rowKey(emp)}
                    onClick={hasSegmentsData ? () => handleSegmentsOpen(emp) : undefined}
                    title={hasSegmentsData ? 'Ver detalle de tramos del día' : undefined}
                    className={`hover:bg-gray-700/30 transition-colors ${
                      hasSegmentsData ? 'cursor-pointer' : ''
                    }`}
                  >
                    {isRange && (
                      <td className="px-4 py-3 text-gray-300 font-mono text-xs whitespace-nowrap">
                        {emp.row_date}
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <div className="font-medium text-white">{emp.employee_name}</div>
                      <div className="text-xs text-gray-500">{emp.workday_id}</div>
                      {emp.pattern_id ? (
                        <div className="text-xs text-blue-300/80">{emp.pattern_name || 'Patrón'}</div>
                      ) : (
                        <div className="text-xs text-amber-300/80">Sin patrón</div>
                      )}
                    </td>

                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-gray-700 text-gray-300 text-xs font-mono">
                        {emp.shift_code || '—'}
                        {hasSegmentsData && <ListTree className="w-3 h-3 text-gray-500 ml-0.5" />}
                      </span>
                    </td>

                    <td className="px-4 py-3 text-gray-300 font-mono text-xs whitespace-nowrap">
                      {emp.planned_start || emp.planned_end
                        ? `${emp.planned_start || '—'} → ${emp.planned_end || '—'}`
                        : '—'}
                    </td>

                    <td className="px-4 py-3 text-center font-mono text-xs whitespace-nowrap">
                      <span className="text-emerald-400">{formatTime(emp.clock_in_raw)}</span>
                      <span className="text-gray-600 mx-1">/</span>
                      <span className="text-rose-400">{formatTime(emp.clock_out_raw)}</span>
                    </td>

                    <td className="px-4 py-3 text-center font-mono text-xs whitespace-nowrap">
                      <span className="text-emerald-400">{formatTime(emp.clock_in_actual)}</span>
                      <span className="text-gray-600 mx-1">/</span>
                      <span className="text-rose-400">{formatTime(emp.clock_out_actual)}</span>
                    </td>

                    <td className="px-4 py-3 text-center">
                      <div className="text-xs text-gray-400 leading-relaxed">
                        <div>{formatOriginCombined(emp.origin_in, emp.origin_out)}</div>
                      </div>
                    </td>

                    <td className="px-4 py-3 text-center">
                      <StatusBadge status={emp.status} isAbsent={emp.is_absent} />
                    </td>

                    <td className="px-4 py-3 text-center">
                      <div className="flex flex-col items-center gap-1">
                        <ValidationBadge status={emp.validation_status} />
                        {isRejected && emp.validation_notes && (
                          <span
                            className="text-xs text-red-400/80 max-w-[120px] truncate"
                            title={emp.validation_notes}
                          >
                            {emp.validation_notes}
                          </span>
                        )}
                      </div>
                    </td>

                    <td className="px-4 py-3 text-gray-400 text-xs max-w-[150px] truncate">
                      {emp.observations || '—'}
                    </td>

                    {canValidate && (
                      <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                        {!showActions && !showCorrect && !showRevert ? (
                          <span className="text-xs text-gray-600">—</span>
                        ) : mutating ? (
                          <Loader2 className="w-4 h-4 animate-spin text-gray-400 mx-auto" />
                        ) : showActions ? (
                          <div className="flex justify-center gap-2">
                            <button
                              type="button"
                              title="Validar fichaje"
                              aria-label="Validar fichaje"
                              onClick={() => handleApproveOpen(emp)}
                              className={`${ACTION_BTN_BASE} border-emerald-600/60 bg-emerald-900/50 text-emerald-300 hover:bg-emerald-700 hover:border-emerald-400 hover:text-white hover:shadow-md hover:shadow-emerald-900/30 focus-visible:ring-emerald-500/50`}
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              title="Rechazar"
                              aria-label="Rechazar fichaje"
                              onClick={() => handleRejectOpen(emp)}
                              className={`${ACTION_BTN_BASE} border-red-700/60 bg-red-950/50 text-red-300 hover:bg-red-800 hover:border-red-500 hover:text-white hover:shadow-md hover:shadow-red-900/30 focus-visible:ring-red-500/50`}
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : showCorrect ? (
                          <button
                            type="button"
                            title="Corregir y validar"
                            aria-label="Corregir y validar fichaje"
                            onClick={() => handleCorrectOpen(emp)}
                            className={`${ACTION_BTN_BASE} border-amber-600/60 bg-amber-900/40 text-amber-200 hover:bg-amber-700 hover:border-amber-400 hover:text-white hover:shadow-md hover:shadow-amber-900/30 focus-visible:ring-amber-500/50 mx-auto`}
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                        ) : (
                          <button
                            type="button"
                            title="Revertir validación"
                            aria-label="Revertir validación"
                            onClick={() => handleRevert(emp)}
                            className={`${ACTION_BTN_BASE} border-gray-600/60 bg-gray-800/80 text-gray-400 hover:bg-gray-600 hover:border-gray-500 hover:text-white hover:shadow-md hover:shadow-black/20 focus-visible:ring-gray-500/50 mx-auto`}
                          >
                            <RotateCcw className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="px-4 py-3 border-t border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4 flex-wrap">
            <p className="text-sm text-gray-500">
              Mostrando {rangeStart}–{rangeEnd} de {totalItems} empleados
            </p>
            <div className="flex items-center gap-2">
              <label htmlFor="attendance-page-size" className="text-sm text-gray-400">
                Mostrar:
              </label>
              <select
                id="attendance-page-size"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
                className="px-3 py-1.5 rounded-lg border border-gray-600 bg-gray-800 text-gray-300 text-sm font-medium hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size} className="bg-gray-800">
                    {size}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, Math.min(p, totalPages) - 1))}
              disabled={currentPage <= 1}
              className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-600 bg-gray-800 text-gray-300 text-sm font-medium hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
              Anterior
            </button>
            <span className="text-sm text-gray-500 px-2">
              Página {currentPage} de {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, Math.min(p, totalPages) + 1))}
              disabled={currentPage >= totalPages}
              className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-600 bg-gray-800 text-gray-300 text-sm font-medium hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Siguiente
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
